import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, User } from 'lucide-react'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getAwaitingPayments } from '../../api/orders'
import { formatMoney, formatDate, paymentStatusLabel, paymentStatusColor } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'

function monthRange(): { due_from: string; due_to: string } {
  const now = new Date()
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  return { due_from: start.toISOString(), due_to: end.toISOString() }
}

export default function PaymentsPage() {
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('period') === 'month' ? 'month' : (searchParams.get('status') ?? 'awaiting_confirmation')
  const [status, setStatus] = useState(initialTab)

  const TABS = [
    { value: 'awaiting_confirmation', label: tr('awaitingConfirmation') },
    { value: 'overdue', label: tr('overdue') },
    { value: 'month', label: tr('thisMonth') },
  ]

  const { data: payments, isLoading } = useQuery({
    queryKey: ['awaiting-payments', status],
    queryFn: () => status === 'month'
      ? getAwaitingPayments({ ...monthRange(), limit: 100 })
      : getAwaitingPayments({ status, limit: 100 }),
    refetchInterval: 30_000,
  })

  return (
    <Layout title={tr('payments')}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TABS.map((t) => {
          const isActive = status === t.value
          return (
            <button
              key={t.value}
              onClick={() => setStatus(t.value)}
              style={{
                padding: '7px 13px', borderRadius: 99, whiteSpace: 'nowrap',
                fontFamily: '"Instrument Sans", sans-serif', fontSize: 12, fontWeight: 500,
                background: isActive ? '#F5F4F0' : 'transparent',
                color: isActive ? '#0A0A0B' : '#A8A8AE',
                border: isActive ? 'none' : '1px solid rgba(255,255,255,0.10)',
              }}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <Spinner className="mt-10" />
      ) : !payments?.length ? (
        <p style={{ textAlign: 'center', color: '#8A8A96', marginTop: 40, fontSize: 14 }}>{tr('noPayments')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {payments.map((p) => {
            const receiptUrl = p.receipt_file_id
              ? (p.receipt_file_id.startsWith('http') ? p.receipt_file_id : `/uploads/${p.receipt_file_id}`)
              : null
            const productName = lang === 'uz' ? p.product_name_uz : p.product_name_ru

            return (
              <div
                key={p.id}
                className="card cursor-pointer active:opacity-70"
                onClick={() => navigate(`/admin/payments/${p.order_id}/${p.id}`)}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    {p.client_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <User size={11} color="#A8A8AE" />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#F5F4F0' }}>{p.client_name}</span>
                        {p.client_phone && <span style={{ fontSize: 11, color: '#8A8A96' }}>· {p.client_phone}</span>}
                      </div>
                    )}
                    <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 18, fontWeight: 600, color: '#F5F4F0' }}>
                      {formatMoney(p.amount)}
                    </p>
                    <p style={{ fontSize: 11, color: '#8A8A96', marginTop: 2 }}>
                      {productName && <span>{productName} · </span>}Платёж #{p.payment_num} · до {formatDate(p.due_date)}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${paymentStatusColor(p.status)}`}>
                      {paymentStatusLabel(p.status, lang)}
                    </span>
                    <ChevronRight size={14} color="#8A8A96" />
                  </div>
                </div>

                {/* Method + date */}
                {p.method && (
                  <p style={{ fontSize: 11, color: '#8A8A96', marginBottom: receiptUrl ? 8 : 0 }}>
                    {p.method === 'cash' ? `💵 ${tr('cash')}` : `🏦 ${tr('transfer')}`}
                    {p.submitted_at && ` · ${formatDate(p.submitted_at)}`}
                  </p>
                )}

                {/* Receipt thumbnail */}
                {receiptUrl && (
                  <img
                    src={receiptUrl}
                    alt="чек"
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginTop: 4 }}
                  />
                )}

                <p style={{ fontSize: 10, color: '#8A8A96', marginTop: 8, textAlign: 'right' }}>
                  Нажмите для подробностей →
                </p>
              </div>
            )
          })}
        </div>
      )}
    </Layout>
  )
}
